// Push notifications opt-in.
//
// Registers the dedicated /push-sw.js service worker, asks for permission,
// fetches the server's VAPID public key, subscribes via the standard
// PushManager, and POSTs the subscription to /api/web-push/subscribe
// with the user's IANA timezone (so the backend can fan out at 7 AM
// local time). Idempotent: re-running it on the same device just
// upserts the existing subscription.
import api from "./api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

export async function ensureSubscribed({ hour = 7 } = {}) {
  if (!pushSupported()) throw new Error("Push notifications aren't supported in this browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied. Enable notifications in your browser settings.");

  // Always register the dedicated worker at /push-sw.js — keeping it
  // separate from any future PWA shell sw avoids scope conflicts.
  const reg = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;

  // Fetch VAPID public key from backend (cheap, cacheable).
  const pkRes = await api.get("/web-push/public-key");
  const { publicKey, enabled } = pkRes.data || {};
  if (!enabled || !publicKey) {
    throw new Error("Push notifications aren't configured on the server yet.");
  }

  // Get existing subscription or create a new one.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  await api.post("/web-push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    timezone: tz,
    hour,
  });
  return { endpoint: json.endpoint, timezone: tz, hour };
}

export async function unsubscribe() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try { await api.post("/web-push/unsubscribe", { endpoint }); } catch (_) {}
    return true;
  } catch (_) {
    return false;
  }
}

export async function sendTestPush() {
  return api.post("/web-push/test");
}
